/**
 * Color and contrast utility for Adiosoft Website Builder
 * Calculates luminance, contrast ratio (WCAG 2.1 AA/AAA compliance),
 * and provides auto-contrast text selection for backgrounds.
 */

// Helper to parse hex or named colors to [r, g, b] (0-255)
export function parseColorToRgb(colorStr: string): [number, number, number] {
  if (!colorStr) return [255, 255, 255];
  const cleaned = colorStr.trim().toLowerCase();

  // Named basics
  if (cleaned === 'transparent') return [255, 255, 255];
  if (cleaned === 'white') return [255, 255, 255];
  if (cleaned === 'black') return [0, 0, 0];

  // Hex format (#RGB or #RRGGBB)
  if (cleaned.startsWith('#')) {
    let hex = cleaned.slice(1);
    if (hex.length === 3) {
      hex = hex.split('').map((char) => char + char).join('');
    }
    if (hex.length >= 6) {
      const r = parseInt(hex.slice(0, 2), 16) || 0;
      const g = parseInt(hex.slice(2, 4), 16) || 0;
      const b = parseInt(hex.slice(4, 6), 16) || 0;
      return [r, g, b];
    }
  }

  // rgb/rgba format
  if (cleaned.startsWith('rgb')) {
    const parts = cleaned.replace(/rgba?\(|\)/g, '').split(',');
    if (parts.length >= 3) {
      const r = Math.min(255, Math.max(0, parseInt(parts[0].trim(), 10) || 0));
      const g = Math.min(255, Math.max(0, parseInt(parts[1].trim(), 10) || 0));
      const b = Math.min(255, Math.max(0, parseInt(parts[2].trim(), 10) || 0));
      return [r, g, b];
    }
  }

  return [255, 255, 255];
}

/**
 * Calculates WCAG 2.1 relative luminance for an sRGB color.
 * Result ranges from 0 (darkest black) to 1 (brightest white).
 */
export function getRelativeLuminance(colorStr: string): number {
  const [r255, g255, b255] = parseColorToRgb(colorStr);

  const normalize = (val: number) => {
    const s = val / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };

  const r = normalize(r255);
  const g = normalize(g255);
  const b = normalize(b255);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculates the contrast ratio between two colors according to WCAG 2.1 specs.
 * Formula: (L1 + 0.05) / (L2 + 0.05) where L1 is the lighter color.
 * Returns a value between 1:1 and 21:1.
 */
export function getContrastRatio(color1: string, color2: string): number {
  const lum1 = getRelativeLuminance(color1);
  const lum2 = getRelativeLuminance(color2);
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Checks if a background color is visually dark (luminance < 0.35).
 */
export function isDarkBackground(colorStr?: string): boolean {
  if (!colorStr) return false;
  return getRelativeLuminance(colorStr) < 0.35;
}

/**
 * Returns either high-contrast light text (#FFFFFF or #F8FAFC) or high-contrast dark text (#0F172A)
 * based on whichever provides the highest contrast against the given background.
 * Guarantees WCAG AA (≥ 4.5:1).
 */
export function getOptimalTextColor(bgColor?: string, fallback = '#0F172A'): string {
  if (!bgColor) return fallback;
  const darkRatio = getContrastRatio(bgColor, '#0F172A');
  const lightRatio = getContrastRatio(bgColor, '#FFFFFF');

  return lightRatio > darkRatio ? '#FFFFFF' : '#0F172A';
}

/**
 * Returns optimal muted text color (#94A3B8 on dark, #64748B on light).
 */
export function getOptimalMutedTextColor(bgColor?: string): string {
  return isDarkBackground(bgColor) ? '#94A3B8' : '#64748B';
}

/**
 * Returns optimal card background and border styling for a section background.
 */
export function getCardStylesForBackground(bgColor?: string) {
  const dark = isDarkBackground(bgColor);
  return {
    isDark: dark,
    cardBg: dark ? 'rgba(255, 255, 255, 0.05)' : '#FFFFFF',
    cardBorder: dark ? 'rgba(255, 255, 255, 0.10)' : 'rgba(0, 0, 0, 0.08)',
    textPrimary: dark ? '#FFFFFF' : '#0F172A',
    textMuted: dark ? '#94A3B8' : '#64748B',
    hoverBg: dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.02)',
    // Tailwind utility classes for direct JSX className composition
    bg: dark ? 'bg-slate-900/80' : 'bg-white',
    border: dark ? 'border-slate-800' : 'border-slate-200/80',
    heading: dark ? 'text-white' : 'text-slate-900',
    mutedText: dark ? 'text-slate-400' : 'text-slate-600',
  };
}

export interface ContrastValidationResult {
  ratio: number;
  ratioFormatted: string;
  isCompliant: boolean;
  status: 'good' | 'warning' | 'error';
  warning: string | null;
  suggestedTextColor: string;
}

/**
 * Validates WCAG 2.1 AA compliance between two colors.
 */
export function validateContrast(
  bgColor?: string,
  textColor?: string,
  minRatio = 4.5
): ContrastValidationResult {
  const bg = bgColor || '#FFFFFF';
  const fg = textColor || '#0F172A';
  const ratio = getContrastRatio(bg, fg);
  const ratioFormatted = `${ratio.toFixed(1)}:1`;
  const isCompliant = ratio >= minRatio;
  const suggestedTextColor = getOptimalTextColor(bg);

  let status: 'good' | 'warning' | 'error' = 'good';
  let warning: string | null = null;

  if (ratio < 3.0) {
    status = 'error';
    warning = `Kritik: Kontrast oranı çok düşük (${ratioFormatted}). Metin neredeyse okunamaz! (WCAG AA hedefi: ≥4.5:1)`;
  } else if (ratio < 4.5) {
    status = 'warning';
    warning = `Uyarı: Kontrast oranı düşük (${ratioFormatted}). Küçük metinlerde okunurluk zorlaşabilir. (WCAG AA hedefi: ≥4.5:1)`;
  }

  return {
    ratio,
    ratioFormatted,
    isCompliant,
    status,
    warning,
    suggestedTextColor,
  };
}

/**
 * Normalized Website Design Model - Single Source of Truth
 */
export interface NormalizedDesignConfig {
  colors: {
    primary: string;
    primaryText: string;
    secondary: string;
    secondaryText: string;
    accent: string;
    background: string;
    surface: string;
    surfaceSecondary: string;
    surfaceBorder: string;
    text: string;
    textSecondary: string;
    textMuted: string;
    heading: string;
    border: string;
    buttonBackground: string;
    buttonText: string;
    inputBackground: string;
    inputText: string;
    inputPlaceholder: string;
    inputBorder: string;
  };
  typography: {
    fontFamily: string;
    headingFont: string;
  };
}

export function normalizeSiteDesign(settings?: any): NormalizedDesignConfig {
  const bg = settings?.backgroundColor || settings?.colors?.background || '#FFFFFF';
  const isDark = isDarkBackground(bg);
  // STRICT ARCHITECTURE: primaryColor is the user's primary choice, accent is secondary/vivid accent
  const primary = settings?.primaryColor || settings?.colors?.primary || settings?.accentColor || '#2563EB';
  const accent = settings?.accentColor || settings?.colors?.accent || primary;
  const secondary = settings?.secondaryColor || settings?.colors?.secondary || (isDark ? '#334155' : '#0F172A');
  const primaryText = getOptimalTextColor(primary);
  const secondaryText = getOptimalTextColor(secondary);

  const text = settings?.colors?.text || (isDark ? '#F8FAFC' : '#0F172A');
  const textSecondary = settings?.colors?.textSecondary || (isDark ? '#CBD5E1' : '#475569');
  const textMuted = settings?.colors?.textMuted || (isDark ? '#94A3B8' : '#64748B');
  const heading = settings?.colors?.heading || (isDark ? '#FFFFFF' : '#0F172A');
  const surface = settings?.colors?.surface || (isDark ? '#0F172A' : '#F8FAFC');
  const surfaceSecondary = settings?.colors?.surfaceSecondary || (isDark ? '#1E293B' : '#F1F5F9');
  const surfaceBorder = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)';
  const border = settings?.colors?.border || (isDark ? '#1E293B' : '#E2E8F0');
  const inputBg = isDark ? '#1E293B' : '#FFFFFF';
  const inputText = isDark ? '#F8FAFC' : '#0F172A';
  const inputPlaceholder = isDark ? '#64748B' : '#94A3B8';
  const inputBorder = isDark ? 'rgba(255, 255, 255, 0.15)' : '#CBD5E1';
  const buttonBg = settings?.colors?.buttonBackground || primary;
  const buttonText = settings?.colors?.buttonText || primaryText;

  const fontFamily = settings?.fontFamily || settings?.typography?.fontFamily || 'Inter, system-ui, sans-serif';
  const headingFont = settings?.headingFont || settings?.typography?.headingFont || 'Plus Jakarta Sans, sans-serif';

  return {
    colors: {
      primary,
      primaryText,
      secondary,
      secondaryText,
      accent,
      background: bg,
      surface,
      surfaceSecondary,
      surfaceBorder,
      text,
      textSecondary,
      textMuted,
      heading,
      border,
      buttonBackground: buttonBg,
      buttonText,
      inputBackground: inputBg,
      inputText,
      inputPlaceholder,
      inputBorder,
    },
    typography: {
      fontFamily,
      headingFont,
    },
  };
}

/**
 * Computes root website CSS variables from SiteSettings or NormalizedDesignConfig.
 * Strictly scoped to .website-renderer!
 */
export function getSiteThemeVariables(settings?: any): Record<string, string> {
  const norm = normalizeSiteDesign(settings);
  const c = norm.colors;

  return {
    '--site-background': c.background,
    '--site-surface': c.surface,
    '--site-surface-secondary': c.surfaceSecondary,
    '--site-surface-border': c.surfaceBorder,
    '--site-text': c.text,
    '--site-text-secondary': c.textSecondary,
    '--site-text-muted': c.textMuted,
    '--site-heading': c.heading,
    '--site-primary': c.primary,
    '--site-primary-text': c.primaryText,
    '--site-secondary': c.secondary,
    '--site-secondary-text': c.secondaryText,
    '--site-accent': c.accent,
    '--site-border': c.border,
    '--site-input-background': c.inputBackground,
    '--site-input-text': c.inputText,
    '--site-input-placeholder': c.inputPlaceholder,
    '--site-input-border': c.inputBorder,
    '--site-button-background': c.buttonBackground,
    '--site-button-text': c.buttonText,
    '--site-font': norm.typography.fontFamily,
    '--site-heading-font': norm.typography.headingFont,
    '--site-link': c.primary,
    '--site-success': '#10B981',
    '--site-warning': '#F59E0B',
    '--site-danger': '#EF4444',
  };
}

export interface SectionThemeResolution {
  isDark: boolean;
  bgColor: string;
  textColor: string;
  headingColor: string;
  mutedColor: string;
  linkColor: string;
  cardBg: string;
  cardText: string;
  cardHeading: string;
  cardBorder: string;
  buttonBg: string;
  buttonText: string;
  inputBg: string;
  inputText: string;
  inputPlaceholder: string;
  inputBorder: string;
  cssVariables: Record<string, string>;
}

/**
 * Resolves full semantic tokens for a specific section, factoring in:
 * 1. Site Theme
 * 2. Section Style Overrides
 * 3. Auto/Manual contrast rules
 */
export function getSectionThemeVariables(
  style?: any,
  settings?: any,
  defaultBg = '#FFFFFF'
): SectionThemeResolution {
  const rawBg = style?.backgroundColor || defaultBg;
  const bgColor = rawBg;
  const isDark = isDarkBackground(bgColor);

  const autoMode = style?.colorMode !== 'manual';

  // Text color resolution
  let textColor: string;
  if (autoMode) {
    textColor = getOptimalTextColor(bgColor);
  } else if (style?.textColor) {
    // If manual, check contrast: if too extreme (ratio < 2.5), auto-adjust to save from white-on-white defect
    const ratio = getContrastRatio(bgColor, style.textColor);
    textColor = ratio < 2.5 ? getOptimalTextColor(bgColor) : style.textColor;
  } else {
    textColor = getOptimalTextColor(bgColor);
  }

  // Heading color resolution
  const headingColor = style?.headingColor || (isDark ? '#FFFFFF' : '#0F172A');

  // Muted text color resolution
  const mutedColor = style?.mutedTextColor || (isDark ? '#94A3B8' : '#64748B');

  // Accent / Primary & Button
  const primary = settings?.primaryColor || settings?.colors?.primary || settings?.accentColor || '#2563EB';
  const buttonBg = style?.buttonBgColor || primary;
  const buttonText = style?.buttonTextColor || getOptimalTextColor(buttonBg);
  const linkColor = style?.linkColor || primary;

  // Cards
  const cardBg = style?.cardBgColor || (isDark ? 'rgba(255, 255, 255, 0.05)' : '#FFFFFF');
  const cardBorder = style?.cardBorderColor || (isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)');
  const cardDark = isDarkBackground(cardBg.startsWith('rgba') && !isDark ? '#FFFFFF' : cardBg);
  const cardHeading = cardDark ? '#FFFFFF' : '#0F172A';
  const cardText = cardDark ? '#CBD5E1' : '#475569';

  // Inputs
  const inputBg = style?.inputBgColor || (isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFFFFF');
  const inputText = style?.inputTextColor || (isDark ? '#FFFFFF' : '#0F172A');
  const inputBorder = style?.inputBorderColor || (isDark ? 'rgba(255, 255, 255, 0.2)' : '#CBD5E1');
  const inputPlaceholder = style?.inputPlaceholderColor || (isDark ? '#94A3B8' : '#94A3B8');

  const cssVariables: Record<string, string> = {
    '--section-bg': bgColor,
    '--section-text': textColor,
    '--section-heading': headingColor,
    '--section-text-muted': mutedColor,
    '--section-link': linkColor,
    '--section-card-bg': cardBg,
    '--section-card-heading': cardHeading,
    '--section-card-text': cardText,
    '--section-card-border': cardBorder,
    '--section-button-bg': buttonBg,
    '--section-button-text': buttonText,
    '--section-input-bg': inputBg,
    '--section-input-text': inputText,
    '--section-input-border': inputBorder,
    '--section-input-placeholder': inputPlaceholder,
    backgroundColor: bgColor,
    color: textColor,
  };

  return {
    isDark,
    bgColor,
    textColor,
    headingColor,
    mutedColor,
    linkColor,
    cardBg,
    cardText,
    cardHeading,
    cardBorder,
    buttonBg,
    buttonText,
    inputBg,
    inputText,
    inputPlaceholder,
    inputBorder,
    cssVariables,
  };
}

